import { useEffect, useRef, useState } from "react";
import { animate, motion, useMotionValue, type PanInfo } from "framer-motion";
import type { Card } from "@solitaire/game-engine";
import { Card as CardComponent } from "@solitaire/ui";
import { useGameStore } from "../store/useGameStore.js";
import { useCardSet } from "../contexts/CardSetContext.js";
import { useFlyAnimation } from "../contexts/FlyAnimationContext.js";
import { dragState, findDropTarget, sharedDragX, sharedDragY } from "../lib/dragState.js";
// ============================================================
// TableauColumn — eine der 7 Tableau-Spalten
// ============================================================
// Peek-Werte kommen per-Set aus dem CardSetContext (Fallback: GAME_CONFIG-Defaults)
const SPRING = { type: "spring", stiffness: 500, damping: 35 } as const;

interface TableauColumnProps {
  columnIndex: number;
}

// ============================================================
// DraggableCard — einzelne Karte
// ============================================================

interface DraggableCardProps {
  card: Card;
  cardIndex: number;
  columnIndex: number;
  isFirst: boolean;
  isSelected: boolean;
  isBeingDragged: boolean;
  isFollowing: boolean;
  dragZIndex: number | null;
  onDragGroupStart: (fromCardIndex: number) => void;
  onDragGroupEnd: () => void;
  /**
   * Geteilter Counter aus TableauColumn — wird bei jedem neuen Drag-Start
   * inkrementiert. Verhindert, dass ein veraltetes Promise.then() aus einem
   * abgebrochenen Rückflug-Spring mitten in einem neuen Drag onDragGroupEnd()
   * aufruft (Race Condition).
   */
  flybackIdRef: { current: number };
}

function DraggableCard({
  card,
  cardIndex,
  columnIndex,
  isFirst,
  isSelected,
  isBeingDragged,
  isFollowing,
  dragZIndex,
  onDragGroupStart,
  onDragGroupEnd,
  flybackIdRef,
}: DraggableCardProps) {
  const {
    gameState,
    applyDrop,
    shakeCardId,
  } = useGameStore();
  const { getFaceUrl, getBackUrl, cardAspectRatio, faceDownPeek, faceUpPeek } = useCardSet();
  const { isFlying } = useFlyAnimation();

  // Lokales MotionValue für Shake-Animation
  const shakeX = useMotionValue(0);

  // Shake-Animation wenn shakeCardId auf diese Karte zeigt
  useEffect(() => {
    if (shakeCardId === card.id) {
      animate(shakeX, [0, -8, 8, -5, 5, 0], { duration: 0.4 });
    }
  }, [shakeCardId, card.id, shakeX]);
  // Eigene MotionValues für die gezogene Karte → ermöglicht manuellen Rückflug
  const dragX = useMotionValue(0);
  const dragY = useMotionValue(0);

  const column = gameState.tableau[columnIndex]!;

  // clamp(): skaliert mit Viewport-Breite, Desktop-Maximum bleibt erhalten.
  // Min-Wert = ~58 % des Desktop-Maximums → skaliert proportional mit dem Peek-Wert.
  // 6vw / 3vw sorgen dafür, dass der Peek auf kleinen Screens (375px) schon ~22px / ~11px beträgt.
  // -R% = -(Höhe/Breite × 100)% → zieht die Karte genau um ihre eigene Höhe nach oben
  const R = `${(cardAspectRatio * 100).toFixed(3)}%`;
  const fuMin = Math.round(faceUpPeek   * 0.58);
  const fdMin = Math.round(faceDownPeek * 0.58);
  const marginTop = isFirst
    ? undefined
    : card.faceUp
      ? `calc(-${R} + clamp(${fuMin}px, 6vw, ${faceUpPeek}px))`
      : `calc(-${R} + clamp(${fdMin}px, 3vw, ${faceDownPeek}px))`;

  const canDrag = card.faceUp && !isFollowing;

  // ─── WICHTIG: Die gezogene Karte benutzt KEIN x/y aus sharedDragX/Y. ──────
  // Framer Motion tracked die Position intern und startet immer bei 0.
  // Via onDrag wird sharedDragX/Y aus info.offset (relativ zum Drag-Start)
  // gespeist, damit Follower-Karten synchron mitlaufen.
  // Das verhindert das Akkumulieren von x-Fehlern über mehrere Drags.
  const motionStyle = isBeingDragged
    ? { marginTop: isFirst ? undefined : marginTop, x: dragX, y: dragY, zIndex: dragZIndex ?? 50 }
    : isFollowing
      ? { marginTop: isFirst ? undefined : marginTop, x: sharedDragX, y: sharedDragY, zIndex: dragZIndex ?? 50 }
      : { marginTop: isFirst ? undefined : marginTop, x: shakeX };

  function handleDragStart() {
    const cards = column.slice(cardIndex);
    if (cards.length === 0 || !cards.every((c) => c.faceUp)) return;
    shakeX.set(0);
    dragX.set(0);
    dragY.set(0);
    sharedDragX.set(0);
    sharedDragY.set(0);
    onDragGroupStart(cardIndex);
    dragState.set({ cards, fromPile: { kind: "tableau", columnIndex } });
  }

  // Synchronisiert Follower mit der gezogenen Karte — info.offset ist immer
  // relativ zum Drag-Start (= 0-basiert, keine Akkumulation über mehrere Drags).
  function handleDrag(_event: PointerEvent, info: PanInfo) {
    sharedDragX.set(info.offset.x);
    sharedDragY.set(info.offset.y);
  }

  function handleDragEnd(event: MouseEvent | TouchEvent | PointerEvent) {
    const clientX = "changedTouches" in event
      ? event.changedTouches[0]?.clientX ?? 0
      : (event as MouseEvent).clientX;
    const clientY = "changedTouches" in event
      ? event.changedTouches[0]?.clientY ?? 0
      : (event as MouseEvent).clientY;

    const target = findDropTarget(clientX, clientY);
    const success = target ? applyDrop(target) : false;

    if (!success) {
      dragState.clear();
      const capturedId = flybackIdRef.current;
      Promise.all([
        animate(dragX, 0, SPRING),
        animate(dragY, 0, SPRING),
        animate(sharedDragX, 0, SPRING),
        animate(sharedDragY, 0, SPRING),
      ]).then(() => {
        if (flybackIdRef.current === capturedId) {
          shakeX.set(0);
          onDragGroupEnd();
        }
      });
    } else {
      shakeX.set(0);
      dragX.set(0);
      dragY.set(0);
      sharedDragX.set(0);
      sharedDragY.set(0);
      onDragGroupEnd();
    }
  }

  return (
    <motion.div
      data-card-id={card.id}
      style={{ ...motionStyle, visibility: isFlying(card.id) ? "hidden" : undefined }}
      className="relative w-full"
      drag={canDrag}
      dragMomentum={false}
      dragElastic={0.05}
      dragTransition={{ bounceStiffness: 500, bounceDamping: 35 }}
      onDragStart={handleDragStart}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      whileDrag={{ scale: 1.02 }}
    >
      <CardComponent
        card={card}
        imageUrl={getFaceUrl(card)}
        backUrl={getBackUrl()}
        selected={isSelected}
        disabled={!card.faceUp}
      />
    </motion.div>
  );
}

// ============================================================
// TableauColumn
// ============================================================

export function TableauColumn({ columnIndex }: TableauColumnProps) {
  const {
    gameState,
    selectedCards,
    selectedFromPile,
    handleEmptyTableauClick,
    handleTableauCardClick,
  } = useGameStore();

  const [dragFromIdx, setDragFromIdx] = useState<number | null>(null);
  const tapStartRef = useRef<{ x: number; y: number } | null>(null);

  // Jeder neue Drag-Start inkrementiert diesen Counter.
  // DraggableCard.handleDragEnd prüft ihn im .then() — verhindert stale Callbacks.
  const flybackIdRef = useRef(0);

  const column = gameState.tableau[columnIndex]!;

  const isFromThisColumn =
    selectedFromPile?.kind === "tableau" &&
    selectedFromPile.columnIndex === columnIndex;

  if (column.length === 0) {
    const hasKingSelected =
      selectedCards !== null &&
      selectedCards.length > 0 &&
      selectedCards[0]!.rank === 13;

    return (
      <div
        className={[
          "relative w-full rounded-md border-2 border-white/20",
          hasKingSelected ? "cursor-pointer border-white/40" : "cursor-default",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ aspectRatio: "2/3" }}
        data-droptarget={`tableau-${columnIndex}`}
        onClick={() => hasKingSelected && handleEmptyTableauClick(columnIndex)}
        role={hasKingSelected ? "button" : "img"}
        aria-label={`Tableau-Spalte ${columnIndex + 1} leer`}
      />
    );
  }

  return (
    <div
      className="relative w-full flex flex-col"
      aria-label={`Tableau-Spalte ${columnIndex + 1}`}
      data-droptarget={`tableau-${columnIndex}`}
      onPointerDownCapture={(e) => {
        tapStartRef.current = { x: e.clientX, y: e.clientY };
      }}
      onPointerUpCapture={(e) => {
        const start = tapStartRef.current;
        tapStartRef.current = null;
        if (!start) return;
        if (Math.abs(e.clientX - start.x) > 5 || Math.abs(e.clientY - start.y) > 5) return;
        const el = (e.target as HTMLElement).closest("[data-card-id]");
        if (!el) return;
        // Inset-Prüfung: Taps am Kartenrand ignorieren (6px horizontal, 4px vertikal)
        const rect = el.getBoundingClientRect();
        const insetX = 6, insetY = 4;
        if (
          e.clientX < rect.left + insetX || e.clientX > rect.right - insetX ||
          e.clientY < rect.top + insetY || e.clientY > rect.bottom - insetY
        ) return;
        const cardId = el.getAttribute("data-card-id");
        const idx = column.findIndex((c) => c.id === cardId);
        if (idx >= 0) {
          handleTableauCardClick(columnIndex, idx);
        }
      }}
    >
      {column.map((card: Card, cardIndex: number) => {
        const selectedStartIndex = isFromThisColumn
          ? column.findIndex((c) => c === selectedCards?.[0])
          : -1;
        const isSelected =
          isFromThisColumn &&
          selectedStartIndex >= 0 &&
          cardIndex >= selectedStartIndex;

        const isBeingDragged = dragFromIdx !== null && cardIndex === dragFromIdx;
        const isFollowing    = dragFromIdx !== null && cardIndex > dragFromIdx;
        const dragZIndex = dragFromIdx !== null && cardIndex >= dragFromIdx
          ? 50 + (cardIndex - dragFromIdx)
          : null;

        return (
          <DraggableCard
            key={card.id}
            card={card}
            cardIndex={cardIndex}
            columnIndex={columnIndex}
            isFirst={cardIndex === 0}
            isSelected={isSelected}
            isBeingDragged={isBeingDragged}
            isFollowing={isFollowing}
            dragZIndex={dragZIndex}
            flybackIdRef={flybackIdRef}
            onDragGroupStart={(fromCardIndex) => {
              flybackIdRef.current += 1;
              setDragFromIdx(fromCardIndex);
            }}
            onDragGroupEnd={() => setDragFromIdx(null)}
          />
        );
      })}
    </div>
  );
}
